import type {
  Asset,
  CurrentAdmin,
  GradeSummary,
  Member,
  MemberDetail,
  MemberInput,
  MemberPhoto,
  MemberRelationship,
  MemberRelationshipInput,
} from "./types";

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    cache: "no-store",
    headers: init?.body instanceof FormData ? undefined : { "Content-Type": "application/json" },
    ...init,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({ error: response.statusText }))) as {
      error?: string;
      details?: unknown;
    };
    throw new ApiError(response.status, body.error ?? response.statusText, body.details);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

function query(params: Record<string, string | number | undefined>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      search.set(key, String(value));
    }
  });
  const text = search.toString();
  return text ? `?${text}` : "";
}

export function fetchGrades() {
  return request<GradeSummary[]>("/api/grades");
}

export function fetchMembers(params: { grade?: string; q?: string } = {}) {
  return request<Member[]>(`/api/members${query(params)}`);
}

export function fetchMemberDetail(slug: string) {
  return request<MemberDetail>(`/api/members/${encodeURIComponent(slug)}`);
}

export function fetchCurrentAdmin() {
  return request<CurrentAdmin>("/api/admin/me");
}

export function fetchAdminMembers(params: { grade?: string; q?: string; status?: string } = {}) {
  return request<Member[]>(`/api/admin/members${query(params)}`);
}

export function fetchAdminMember(id: string) {
  return request<MemberDetail>(`/api/admin/members/${encodeURIComponent(id)}`);
}

export function createMember(input: MemberInput) {
  return request<Member>("/api/admin/members", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateMember(id: string, input: MemberInput) {
  return request<Member>(`/api/admin/members/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function uploadAsset(file: File, kind: "avatar" | "photo", alt?: string) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("kind", kind);
  if (alt) {
    formData.append("alt", alt);
  }
  return request<Asset>("/api/admin/assets", {
    method: "POST",
    body: formData,
  });
}

export function attachMemberPhoto(memberId: string, input: { assetId: string; caption?: string }) {
  return request<MemberPhoto>(`/api/admin/members/${encodeURIComponent(memberId)}/photos`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function deleteMemberPhoto(memberId: string, photoId: string) {
  return request<void>(
    `/api/admin/members/${encodeURIComponent(memberId)}/photos/${encodeURIComponent(photoId)}`,
    { method: "DELETE" },
  );
}

export function setMemberCoverPhoto(memberId: string, photoId: string) {
  return request<void>(
    `/api/admin/members/${encodeURIComponent(memberId)}/photos/${encodeURIComponent(photoId)}/cover`,
    { method: "POST" },
  );
}

export function createMemberRelationship(memberId: string, input: MemberRelationshipInput) {
  return request<MemberRelationship>(`/api/admin/members/${encodeURIComponent(memberId)}/relationships`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateMemberRelationship(memberId: string, relationshipId: string, input: MemberRelationshipInput) {
  return request<MemberRelationship>(
    `/api/admin/members/${encodeURIComponent(memberId)}/relationships/${encodeURIComponent(relationshipId)}`,
    {
      method: "PUT",
      body: JSON.stringify(input),
    },
  );
}

export function deleteMemberRelationship(memberId: string, relationshipId: string) {
  return request<void>(
    `/api/admin/members/${encodeURIComponent(memberId)}/relationships/${encodeURIComponent(relationshipId)}`,
    { method: "DELETE" },
  );
}
